import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import {PaidButtonLabel, SpreadsheetActionNavigation, paidSubmitButtonSx} from "./SpreadsheetActionControls";
import SearchIcon from "@mui/icons-material/Search";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import TollIcon from "@mui/icons-material/Toll";
import DataEnrichmentIdentityIcon from "./DataEnrichmentIdentityIcon";
import { SIDEBAR } from "../../components/CompanyModal/v2/sidebarVisual";
import ActionCreditUsageBanner from "../../components/layouts/ActionCreditUsageBanner";

const DATA_ENRICHMENT_COST_LABEL = '1 credit / row';

const FILTER_ICON_SX = {
  fontSize: 18,
  color: 'text.secondary',
};

const AI_CREDIT_CHIP_SX = {
  height: 24,
  fontSize: '11px',
  fontWeight: 700,
  bgcolor: 'rgba(76, 175, 80, 0.1)',
  color: '#4caf50',
  flexShrink: 0,
  '& .MuiChip-icon': {
    ml: '6px',
    color: '#4caf50',
  },
  '& .MuiChip-icon .MuiSvgIcon-root': {
    color: '#4caf50',
  },
  '& .MuiChip-label': { px: 0.85 },
};

const IDENTITY_ICON_SIZE = 27;

const DATA_ENRICHMENT_OPTIONS = [
  {
    id: 'find_and_verify_email',
    title: 'Find Verified Emails',
    description: 'Find and verify emails.',
    Icon: SearchIcon, costLabel: DATA_ENRICHMENT_COST_LABEL,
  },
  {
    id: 'verify_email',
    title: 'Verify Existing Emails',
    description: 'Verify deliverability of existing emails using Hunter',
    Icon: MarkEmailReadIcon, costLabel: '1 credit / email',
  },
];

function DataEnrichmentView({
  theme,
  selection,
  onSelectionChange,
  onBack,
  onClose,
  onContinue,
  hasCreditShortfall = false,
  creditBalanceResolved = false,
  selectedCount = 0, creditUnitCount = selectedCount, costSuffix = '',
  appliesLabelOverride = null,
}) {
  const CONTROL_HEIGHT = 40;
  const isDark = theme.palette.mode === 'dark';
  const isInvalidSelection = selectedCount === 0;
  const submitDisabled = !selection
    || isInvalidSelection
    || !creditBalanceResolved
    || hasCreditShortfall;
  const cardSurface = isDark ? 'rgba(255,255,255,0.06)' : SIDEBAR.surfaceReadonly;
  const cardHover = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(88, 89, 91, 0.07)';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.10)' : SIDEBAR.borderSoft;
  const selectedBorder = `1px solid ${theme.palette.primary.main}`;

  const appliesLabel = appliesLabelOverride
    || (isInvalidSelection
      ? 'Select at least 1 company.'
      : `Applies to ${selectedCount} selected ${selectedCount === 1 ? 'company' : 'companies'}`);

  return (
    <Box

      data-testid="spreadsheet-data-enrichment-menu"
      sx={{ width: 'min(520px, calc(100vw - 32px))', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.75,
          py: 1.1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15, minWidth: 0 }}>
          <DataEnrichmentIdentityIcon size={IDENTITY_ICON_SIZE} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            Data Enrichment
          </Typography>
        </Box>
        <SpreadsheetActionNavigation onBack={onBack} onClose={onClose} />
      </Box>

      <Box
        sx={{
          px: 1.75,
          pt: 1.5,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          bgcolor: isDark ? 'background.default' : '#FDFDFD',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '12.5px' }}>
          Find or verify company emails.
        </Typography>

        <Box
          role="group"
          aria-label="Enrichment action"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: SIDEBAR.gap.listItem,
            minWidth: 0,
          }}
        >
          {DATA_ENRICHMENT_OPTIONS.map(({ id, title, description, costLabel, Icon }) => {
            const selected = selection === id;
            return (
              <Box
                key={id}
                component="button"
                type="button"
                data-testid={`data-enrichment-option-${id}`}
                data-selected={selected ? 'true' : 'false'}
                aria-pressed={selected}
                onClick={() => onSelectionChange(selected ? null : id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: SIDEBAR.radiusSm,
                  border: selected ? selectedBorder : cardBorder,
                  bgcolor: cardSurface,
                  boxShadow: 'none',
                  px: 1.75,
                  py: SIDEBAR.cardPadY,
                  m: 0,
                  appearance: 'none',
                  font: 'inherit',
                  color: 'inherit',
                  transition: 'border-color 160ms ease, background-color 160ms ease',
                  '&:hover': {
                    bgcolor: cardHover,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: SIDEBAR.radiusSm,
                    bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    flexShrink: 0,
                    mr: 1.75,
                    '& .MuiSvgIcon-root': FILTER_ICON_SX,
                  }}
                >
                  <Icon />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, pr: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 13,
                      lineHeight: 1.25,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: 11.5,
                      fontWeight: 500,
                      lineHeight: 1.35,
                      color: 'text.secondary',
                    }}
                  >
                    {description}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  icon={<TollIcon sx={{ fontSize: '14px !important' }} />}
                  label={costLabel}
                  sx={AI_CREDIT_CHIP_SX}
                />
              </Box>
            );
          })}
        </Box>

        <ActionCreditUsageBanner actionId={selection} selectedCount={creditUnitCount} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            width: '100%',
            pt: 0.25,
            height: CONTROL_HEIGHT,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: '1 1 auto',
              height: '100%',
            }}
          >
            <Typography
              variant="caption"
              color={isInvalidSelection && !appliesLabelOverride ? 'error.main' : 'text.secondary'}
              sx={{
                textAlign: 'left',
                fontWeight: isInvalidSelection && !appliesLabelOverride ? 700 : 400,
                fontSize: '11.5px',
                lineHeight: 1,
                m: 0,
                display: 'block',
              }}
            >
              {appliesLabel}
            </Typography>
          </Box>
          <Button
            variant="contained"
            disableElevation
            disabled={submitDisabled}
            data-testid="data-enrichment-continue"
            onClick={() => {
              if (submitDisabled) return;
              onContinue(selection);
            }}
            sx={paidSubmitButtonSx(theme, costSuffix)}
          >
            <PaidButtonLabel label="Continue" cost={costSuffix} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export { DataEnrichmentView, FILTER_ICON_SX, AI_CREDIT_CHIP_SX, IDENTITY_ICON_SIZE, DATA_ENRICHMENT_OPTIONS, DATA_ENRICHMENT_COST_LABEL };
